import * as React from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { withStyles } from "@material-ui/core/styles";

import StyledToolTip from "@StyledToolTip";
import headerStyles from "../../../layout/layoutHeader/components/styles";

export interface ToolbarIconWithLoaderProps {
  /** Toolbar glyph (e.g. Material-UI icon). Color follows theme icon color via wrapper. */
  icon: React.ReactNode;
  tooltipTitle: string;
  /** When true, click is enabled (subject to `loading`). */
  enabled: boolean;
  /** Primary ring spinner around the icon. */
  loading?: boolean;
  onClick: () => void;
  /** Spinner diameter in px. */
  ringSize?: number;
  /** Extra class names on the outer clickable div (e.g. `glHeaderIcons`). */
  rootClassName?: string;
}

const DEFAULT_RING = 32;

const styles = (theme: any) => {
  const { headerIconStyles, iconLayout, enableIcon, disableIcon } = (
    headerStyles as (t: any) => any
  )(theme);

  return {
    headerIconStyles: {
      ...headerIconStyles,
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      float: "left",
    },
    iconLayout: {
      ...iconLayout,
      "&>span": {
        padding: 0,
        color: theme.iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    enableIcon,
    disableIcon,
    rootHitArea: {
      minWidth: "calc(var(--toolbar-icon-ring-size) + 12px)",
      minHeight: "calc(var(--toolbar-icon-ring-size) + 2px)",
    },
    iconWrap: {
      position: "relative" as const,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "var(--toolbar-icon-ring-size)",
      height: "var(--toolbar-icon-ring-size)",
    },
    progressRing: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      marginTop: "calc(var(--toolbar-icon-ring-size) / -2)",
      marginLeft: "calc(var(--toolbar-icon-ring-size) / -2)",
      color: theme.palette.primary.main,
      width: "var(--toolbar-icon-ring-size) !important",
      height: "var(--toolbar-icon-ring-size) !important",
    },
    iconCenter: {
      position: "relative" as const,
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 0,
      color: theme.iconColor,
    },
  };
};

type InnerProps = ToolbarIconWithLoaderProps & {
  classes: any;
};

const ToolbarIconWithLoaderInner = ({
  classes,
  icon,
  tooltipTitle,
  enabled,
  loading = false,
  onClick,
  ringSize = DEFAULT_RING,
  rootClassName = "",
}: InnerProps) => {
  const clickable = enabled && !loading;
  const rootClasses = [
    classes.headerIconStyles,
    classes.iconLayout,
    classes.rootHitArea,
    clickable ? classes.enableIcon : classes.disableIcon,
    rootClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const ringSizeVar = {
    ["--toolbar-icon-ring-size" as string]: `${ringSize}px`,
  } as React.CSSProperties;

  return (
    <div
      className={rootClasses}
      style={ringSizeVar}
      onClick={clickable ? onClick : undefined}
      role="presentation"
    >
      <StyledToolTip title={tooltipTitle}>
        <div className={classes.iconWrap}>
          {loading ? (
            <CircularProgress className={classes.progressRing} />
          ) : null}
          <div className={classes.iconCenter}>{icon}</div>
        </div>
      </StyledToolTip>
    </div>
  );
};

export default withStyles(styles as any)(ToolbarIconWithLoaderInner as React.ComponentType<any>);
